/**
 * PlayPanel — THE GAME.
 *
 * The player is the Red Team. Pick a scenario, set the compromise budget k,
 * compromise up to k agents, then attempt to construct a forbidden
 * cross-agent square by clicking a board edge. The legal-move generator
 * (algorithms/rules.ts) decides what is offerable; the reducer
 * (algorithms/model.ts) drives the finite-state machine. There is no render
 * loop and no game logic in this component — it renders state and dispatches
 * intents.
 *
 * The structural win: the honest core is unbreachable. The honest-to-honest
 * composite is never offered — it is structurally inexpressible, as opposed
 * to a guard catching a move that otherwise would have worked.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React, { useMemo, useReducer, useState } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import { AgentNetworkBoard } from "../components/AgentNetworkBoard.js";
import { SCENARIOS, scenarioById } from "../data/scenarios.js";
import type { AgentId, GameIntent, GameState } from "../algorithms/types.js";
import { initialState, maxBudget, reducer } from "../algorithms/model.js";
import {
  coverage,
  pairKey,
  surfaceBound,
  violationSurface,
} from "../algorithms/rules.js";
import { deriveProgression } from "../algorithms/progression.js";
export function PlayPanel(): React.JSX.Element {
  const [scenarioId, setScenarioId] = useState<string>(SCENARIOS[0].id);
  const scenario = useMemo(() => scenarioById(scenarioId), [scenarioId]);
  const [state, dispatch] = useReducer(
    (prev: GameState, intent: GameIntent) => reducer(scenario, prev, intent),
    scenario,
    (s) => initialState(s, 1),
  );
  // Reset the machine whenever the scenario changes.
  React.useEffect(() => {
    dispatch({ kind: "reset" });
    // The reducer closes over `scenario`; reset rebuilds from it.
  }, [scenario]);
  const [selected, setSelected] = useState<AgentId | null>(null);
  const compromisedSet = useMemo(
    () => new Set(state.compromised),
    [state.compromised],
  );
  const realizedKeys = useMemo(
    () => new Set(state.realized.map((v) => pairKey(v.a, v.b))),
    [state.realized],
  );
  const reachable = useMemo(
    () => violationSurface(scenario, compromisedSet),
    [scenario, compromisedSet],
  );
  const bound = useMemo(
    () => surfaceBound(scenario, compromisedSet),
    [scenario, compromisedSet],
  );
  const cover = coverage(reachable.length, state.realized.length);
  const progression = useMemo(
    () => deriveProgression(state, reachable.length),
    [state, reachable.length],
  );
  const onToggleAgent = (id: AgentId): void => {
    setSelected(id);
    if (compromisedSet.has(id)) {
      dispatch({ kind: "restore", agent: id });
    } else {
      dispatch({ kind: "compromise", agent: id });
    }
  };
  const onAttemptEdge = (a: AgentId, b: AgentId): void => {
    dispatch({ kind: "realize", a, b });
  };
  const honestCount = scenario.agents.length - state.compromised.length;
  return (
    <PanelSurface.PanelShell
      title="Red Team"
      subtitle="Compromise up to k agents and try to breach the honest core. You can't — the honest-to-honest violation is not in the move set."
      accentColor={panelTokens.accent}
    >
      <PanelSurface.Callout title="The honest core is unbreached" tone="info">
        No Red Team strategy breaches the honest sub-system. Every square you
        can reach factors through an agent you compromised; the honest-to-honest
        composite is{" "}
        <strong className="text-[var(--adv-bright)]">
          structurally inexpressible
        </strong>{" "}
        — it never appears as an offerable move. Your score is how much of the
        bounded adversary-reachable surface you explored, not threats blocked.
      </PanelSurface.Callout>

      <PanelSurface.SectionHeading>Scenario</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.ChipRow
          ariaLabel="Agent-network scenario"
          activeId={scenarioId}
          onChange={(id) => {
            setScenarioId(id);
            setSelected(null);
          }}
          items={SCENARIOS.map((s) => ({
            id: s.id,
            label: s.title,
            color: panelTokens.accent,
          }))}
        />
        <p className="mx-1 mt-3 mb-0 text-[12.5px] leading-[1.6] text-[var(--adv-muted)]">
          {scenario.summary} FORBID class:{" "}
          <span className="text-[var(--adv-adversary)]">
            {scenario.forbidLabel}
          </span>
          .
        </p>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Compromise budget · k</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[12.5px] text-[var(--adv-muted)]">
            k = {String(state.budget)} of {String(maxBudget(scenario))} max (n ={" "}
            {String(scenario.agents.length)})
          </span>
          <PanelSurface.ControlBar
            className="ml-auto"
            ariaLabel="Set compromise budget k"
          >
            {Array.from({ length: maxBudget(scenario) + 1 }, (_, k) => (
              <PanelSurface.ChoiceChip
                key={k}
                id={String(k)}
                label={String(k)}
                active={state.budget === k}
                role="button"
                color={panelTokens.adversary}
                onSelect={() => {
                  dispatch({ kind: "setBudget", budget: k });
                }}
              />
            ))}
          </PanelSurface.ControlBar>
        </div>
        <p className="mx-0 mt-2.5 mb-0 text-[12px] leading-[1.6] text-[var(--adv-muted)]">
          Raising k grows the adversary-reachable surface but it stays bounded
          (HUD below). k is capped strictly below n, so an honest core always
          remains.
        </p>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>The board</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <p className="mx-0 mt-0 mb-3 text-[12.5px] leading-[1.6] text-[var(--adv-text)]">
          Click an agent to compromise it (circle to red diamond) or restore it.
          Solid amber edges are reachable forbidden squares — click one to
          realize it. Dashed blue edges are honest governed interfaces: no
          forbidden square is constructable across them.
        </p>
        <AgentNetworkBoard
          scenario={scenario}
          compromised={compromisedSet}
          realizedKeys={realizedKeys}
          selected={selected}
          onToggleAgent={onToggleAgent}
          onAttemptEdge={onAttemptEdge}
        />
        <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-[var(--adv-muted)]">
          <LegendDot color={panelTokens.honest} shape="circle" label="honest agent" />
          <LegendDot
            color={panelTokens.adversary}
            shape="diamond"
            label="compromised (adversarial)"
          />
          <LegendDot
            color={panelTokens.surfaceBound}
            shape="line"
            label="reachable forbidden square"
          />
          <LegendDot
            color={panelTokens.interface}
            shape="dash"
            label="honest interface (inexpressible)"
          />
        </div>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>HUD</PanelSurface.SectionHeading>
      <PanelSurface.KpiGrid>
        <PanelSurface.KpiBox
          label="Red Team score"
          value={String(progression.score)}
          color={panelTokens.adversary}
          subtitle={progression.rank}
        />
        <PanelSurface.KpiBox
          label="Compromised · k used"
          value={`${String(state.compromised.length)} / ${String(state.budget)}`}
          color={panelTokens.adversary}
          subtitle={`${String(honestCount)} agents still honest`}
        />
        <PanelSurface.KpiBox
          label="Realized surface"
          value={String(state.realized.length)}
          color={panelTokens.surfaceBound}
          subtitle="forbidden squares you constructed"
        />
        <PanelSurface.KpiBox
          label="Reachable surface"
          value={String(reachable.length)}
          color={panelTokens.surfaceBound}
          subtitle="squares confined to your reach"
        />
        <PanelSurface.KpiBox
          label="Surface bound · §4.4"
          value={String(bound)}
          color={panelTokens.accentBlue}
          subtitle="k·a³ + k²·a²·A ceiling"
        />
        <PanelSurface.KpiBox
          label="Argument coverage"
          value={`${String(Math.round(cover * 100))}%`}
          color={panelTokens.honest}
          subtitle="of the bounded reachable surface"
        />
        <PanelSurface.KpiBox
          label="Honest core"
          value="UNBREACHED"
          color={panelTokens.honest}
          subtitle="by construction · for all k"
        />
      </PanelSurface.KpiGrid>

      <PanelSurface.SectionHeading>Mission board</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <div className="flex flex-col gap-2.5">
          {progression.missions.map((mission) => (
            <div
              key={mission.id}
              className="rounded-[10px] border border-solid px-3 py-2.5"
              style={{
                background: mission.completed
                  ? `${panelTokens.honest}14`
                  : `${panelTokens.interface}10`,
                borderColor: mission.completed
                  ? `${panelTokens.honest}55`
                  : `${panelTokens.interface}44`,
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12.5px] font-semibold text-[var(--adv-bright)]">
                  {mission.title}
                </span>
                <span
                  className="font-mono text-[11.5px]"
                  style={{
                    color: mission.completed
                      ? panelTokens.honest
                      : panelTokens.surfaceBound,
                  }}
                >
                  {mission.completed
                    ? "COMPLETE"
                    : `${String(Math.round(mission.progress * 100))}%`}
                </span>
              </div>
              <div className="mt-1 text-[12px] leading-[1.55] text-[var(--adv-muted)]">
                {mission.detail}
              </div>
            </div>
          ))}
        </div>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Status</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <div
          aria-live="polite"
          className="font-mono text-[12.5px] leading-[1.65] text-[var(--adv-text)]"
        >
          {state.status}
        </div>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Verdict</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.VerdictBanner
          tone="safe"
          title="Honest sub-system safe — no breach is constructable"
          body={
            reachable.length === 0
              ? "With no agents compromised, the reachable violation surface is empty: every interface is honest-to-honest, so there is no forbidden square to construct. The honest-agent isolation lemma (RD-31 §4.2, resting on RD-05's mechanized compositional-safety) holds."
              : `You hold ${String(state.compromised.length)} compromised agent${state.compromised.length === 1 ? "" : "s"}. The reachable surface (${String(reachable.length)}) is confined to squares factoring through them and stays ≤ the §4.4 bound (${String(bound)}). The honest core remains unbreached: no purely honest composite is in the move set.`
          }
        />
      </PanelSurface.Card>

      {state.realized.length > 0 && (
        <>
          <PanelSurface.SectionHeading>
            Your confined surface ({String(state.realized.length)})
          </PanelSurface.SectionHeading>
          <PanelSurface.Card>
            <div className="flex flex-col gap-1.5">
              {state.realized.map((v) => (
                <div
                  key={pairKey(v.a, v.b)}
                  className="rounded-[10px] border border-solid px-3 py-2.5 font-mono text-[12.5px] text-[var(--adv-text)]"
                  style={{
                    background: `${panelTokens.adversary}10`,
                    borderColor: `${panelTokens.adversary}55`,
                  }}
                >
                  <span className="font-bold text-[var(--adv-adversary)]">
                    {v.a} → {v.b}
                  </span>{" "}
                  factors through {v.factorsThrough.join(", ")}{" "}
                  {PanelSurface.badge("confined", panelTokens.surfaceBound)}
                </div>
              ))}
            </div>
          </PanelSurface.Card>
        </>
      )}
    </PanelSurface.PanelShell>
  );
}
function LegendDot({
  color,
  shape,
  label,
}: {
  color: string;
  shape: "circle" | "diamond" | "line" | "dash";
  label: string;
}): React.JSX.Element {
  return (
    <span className="flex items-center gap-1.5">
      <svg width={16} height={16} aria-hidden>
        {shape === "circle" && (
          <circle
            cx={8}
            cy={8}
            r={6}
            fill={`${color}22`}
            stroke={color}
            strokeWidth={2}
          />
        )}
        {shape === "diamond" && (
          <rect
            x={3}
            y={3}
            width={10}
            height={10}
            transform="rotate(45 8 8)"
            fill={`${color}22`}
            stroke={color}
            strokeWidth={2}
          />
        )}
        {shape === "line" && (
          <line x1={1} y1={8} x2={15} y2={8} stroke={color} strokeWidth={3} />
        )}
        {shape === "dash" && (
          <line
            x1={1}
            y1={8}
            x2={15}
            y2={8}
            stroke={color}
            strokeWidth={2}
            strokeDasharray="3 3"
          />
        )}
      </svg>
      {label}
    </span>
  );
}
